import { Component, h } from 'preact'
import Panel from '../panel'
import IntegrationHeader from './IntegrationHeader'
import SalesforceLogo from './assets/salesforceLogo.js'
import ButtonControl from '../button-control'
import axios from 'axios'
import RadioInputGroup from '../ui-components/RadioInputGroup'
import InputGroup from '../ui-components/InputGroup'
import AlertComponent from '../ui-components/AlertComponent'
import ConfirmationModal from '../ui-components/ConfirmationModal'
import globals from '../../utils/globals'
import { isHtmlElement } from '../../libs/preact-entry'
import Header from '../header'

interface Props {
   csrfToken: string
   isIntegrationsHomepage: boolean
}

interface SalesforceData {
    type: string,
    username: string,
    instanceUrl: string,
    appConsumerKey: string
}

interface State extends SalesforceData {
    isLoading?: boolean,
    isInstalled: boolean,
    messageType?: string|null,
    message?: string,
    showConfirmationMessage: boolean
}

class SalesforceIntegration extends Component<Props, State> {
  downloadLink: string
  headerDescription: string
  isSalesforceEnabled: boolean

  constructor (props: Props) {
    super(props)
    this.state = {
      isLoading: false,
      type: '',
      username: '',
      instanceUrl: '',
      appConsumerKey: '',
      isInstalled: false,
      messageType: null,
      message: '',
      showConfirmationMessage: false
    }
    this.headerDescription = 'Push community member and activity data to Salesforce to gain insights via a 360 degree customer view.'
    this.downloadLink = '/integration/salesforce/download'
    this.isSalesforceEnabled = ((globals('activeModules') || []).indexOf('salesforce') !== -1)
    this.onTypeChanged = this.onTypeChanged.bind(this)
    this.onUsernameChanged = this.onUsernameChanged.bind(this)
    this.onInstanceUrlChanged = this.onInstanceUrlChanged.bind(this)
    this.onAppConsumerKeyChanged = this.onAppConsumerKeyChanged.bind(this)
    this.onSave = this.onSave.bind(this)
    this.onShowConfirmationMessage = this.onShowConfirmationMessage.bind(this)
    this.onHideConfirmationMessage = this.onHideConfirmationMessage.bind(this)
    this.onDelete = this.onDelete.bind(this)
  }

  async componentDidMount () {
    if (!this.isSalesforceEnabled) {
      return
    }
    this.setState({ isLoading: true, messageType: null })
    try {
      const res = await axios.get('/integration/salesforce/settings', {
        validateStatus: (state: number) => {
          return (state >= 200 && state < 300) || state === 403
        }
      })
      if (res.data) {
        this.setState({
          type: res.data.type,
          username: res.data.username,
          instanceUrl: res.data.instanceUrl,
          appConsumerKey: res.data.appConsumerKey,
          isInstalled: res.data.isInstalled
        })
      }
      this.setState({ isLoading: false })
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Unable to retrieve salesforce configuration.', isLoading: false })
    }
  }

  onTypeChanged (value: string): void {
    this.setState({ type: value })
  }

  onUsernameChanged (e: ChangeEvent) {
    this.setState({ username: e.target.value })
  }

  onInstanceUrlChanged (e: ChangeEvent) {
    this.setState({ instanceUrl: e.target.value })
  }

  onAppConsumerKeyChanged (e: ChangeEvent) {
    this.setState({ appConsumerKey: e.target.value })
  }

  onShowConfirmationMessage () {
    this.setState({ showConfirmationMessage: true })
  }

  onHideConfirmationMessage () {
    this.setState({ showConfirmationMessage: false })
  }

  handleOpenEmbeddables () {
    const embeddablesButton = document.querySelector('.insided-conversational-trigger-button')

    if (isHtmlElement(embeddablesButton)) {
      embeddablesButton.click()
    }
  }

  async onDelete () {
    this.setState({ isLoading: true, messageType: null })
    const errorMessage = 'Unable to delete credentials for salesforce integration.'
    try {
      await axios.delete('/integration/salesforce/settings', { data: { csrfToken: this.props.csrfToken } })
      this.setState({
        type: '',
        username: '',
        instanceUrl: '',
        appConsumerKey: '',
        isInstalled: false,
        isLoading: false
      })
    } catch (e) {
      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
    this.onHideConfirmationMessage()
  }

  async onSave () {
    this.setState({ messageType: null, isLoading: true })
    const successMessage = 'Salesforce integration is installed successfully!'
    let errorMessage: string = 'Unable to save configuration for salesforce integration.'
    try {
      await axios.post('/integration/salesforce/settings', {
        type: this.state.type,
        username: this.state.username,
        instanceUrl: this.state.instanceUrl,
        appConsumerKey: this.state.appConsumerKey,
        csrfToken: this.props.csrfToken
      })
      this.setState({ isInstalled: true, messageType: 'success', message: successMessage, isLoading: false })
    } catch (e) {
      if (e.response.data) {
        errorMessage = e.response.data.error.reduce((accum: string, item: string) => {
          accum += '<br>' + item
          return accum
        })
      }

      this.setState({ messageType: 'error', message: errorMessage, isLoading: false })
    }
  }

  render (props: Props, state: State) {
    const types = ['Sandbox', 'Production']
    const isVisibleFor = state.messageType === 'success' ? 5000 : false
    const getDeleteConfirmationButtons = () => {
      return [
        {
          text: 'Confirm',
          class: 'btn btn-danger qa-confirm',
          showLoading: true,
          callback: this.onDelete
        },
        {
          text: 'Cancel',
          class: 'btn btn-default',
          showLoading: false,
          callback: this.onHideConfirmationMessage
        }
      ]
    }

    if (props.isIntegrationsHomepage) {
      return (
        <div class='integrations-salesforce'>
          <Panel title={<IntegrationHeader isInstalled={state.isInstalled} name='Salesforce'
          description={this.headerDescription}><SalesforceLogo width={55} height={55} /></IntegrationHeader>}></Panel>
        </div>
      )
    }

    return (
      <div class='integrations-salesforce'>
        {!props.isIntegrationsHomepage &&
          <Header
            title='Salesforce'
            disableCancelButton
            disableSaveButton
            hasBreadcrumb
            breadcrumbText='Back to integrations overview'
            breadcrumbLink='/settings/integrations'
            statusLabel={state.isInstalled ? 'Installed' : ''}
          />
        }
        <Panel title='Integration setup'>
          {
            state.messageType ? <AlertComponent messageType={state.messageType} message={state.message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {!this.isSalesforceEnabled
            ? (<div className='qa-salesforce-integration-disabled'> The Salesforce integration is part of our
                Enterprise package. Please contact your CSM (or Support) to learn more. </div>)
            : (<div>
              <p className='integrations-salesforce__info'>Learn how to install the Salesforce integration step by
                  step. <button className='salesforce-configuration__note-text-btn' type='button' onClick={this.handleOpenEmbeddables}>Learn more</button></p>
              <a href={this.downloadLink} target='_blank'> <ButtonControl
                title='Download certificate' /> </a>
              <form className='integrations-salesforce__form' onSubmit={(e) => { e.preventDefault(); this.onSave() }}>
                <RadioInputGroup
                  title='Salesforce instance type *'
                  required
                  name='type'
                  disabled={state.isInstalled || state.isLoading ? 'disabled' : ''}
                  options={types}
                  value={state.type}
                  onChange={this.onTypeChanged}
                />
                <InputGroup title='Salesforce username *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.username}
                  onChange={this.onUsernameChanged}
                  qaClasses='qa-salesforce-username-input' />
                <InputGroup title='Instance URL *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.instanceUrl}
                  onChange={this.onInstanceUrlChanged}
                  qaClasses='qa-salesforce-instance-url-input' />
                <InputGroup title='Connected app consumer key *'
                  type='text'
                  required
                  readOnly={state.isInstalled || state.isLoading ? 'readonly' : ''}
                  value={state.appConsumerKey}
                  onChange={this.onAppConsumerKeyChanged}
                  qaClasses='qa-salesforce-app-consumer-key-input' />
                {state.isInstalled ? <ButtonControl variant='warning' class={'qa-delete'} title='Uninstall and delete credentials' onClick={this.onShowConfirmationMessage} />
                  : <ButtonControl variant='primary' type='submit' class={'qa-salesforce-submit'} title='Install' isLoading={state.isLoading} />}
              </form>
              <ConfirmationModal
                isLoading={state.isLoading}
                enableModal={state.showConfirmationMessage}
                title='Uninstall Salesforce integration?'
                message='Community data will no longer be pushed to your Salesforce instance.'
                buttons={getDeleteConfirmationButtons()}
                onClose={this.onHideConfirmationMessage}
              />
            </div>)
          }
        </Panel>
      </div>
    )
  }
}

export default SalesforceIntegration
